<template>
  <v-row class="">
    <v-col cols="2">
      <v-card
          class="agency-card cursor-pointer pa-0"
          :class="{
            'agency-card--inactive': agency.status !== 'validated',
            'agency-card--linked-hover': isConfirmHover
          }"
          outlined
          @mouseenter="startConfirmHover"
          @mouseleave="stopConfirmHover"
          @click="editable ? $emit('confirm') : $emit('edit')"
      >
        <v-card-text class="pt-2">
          <div class="mb-2">
            <div class="agency-card__icon">
              <v-icon>mdi-home-outline</v-icon>
            </div>
          </div>

          <div class="">
            <div class="agency-card__name font-weight-medium  cyan--text text--darken-3">
              {{ agency?.name }}
            </div>
            <div class="agency-card__email grey--text text--darken-1 mt-1">
              {{ agency?.email }}
            </div>
          </div>

          <div class="d-flex flex-column mt-3">
            <div v-if="agency?.street" class="agency-card__line grey--text text--darken-3">
              {{ agency.street }}
            </div>

            <div class="agency-card__line">
              <span v-if="agency?.postalcode">{{ agency.postalcode }}</span>
              <span v-if="agency?.city" class="ml-1">{{ agency.city }}</span>
            </div>

            <div v-if="agency?.country" class="agency-card__line">
              {{ agency.country }}
            </div>
          </div>
        </v-card-text>
      </v-card>

      <div v-if="editable" class="d-flex flex-column mt-2">
        <div class="d-flex align-center justify-center mb-3">
          <button class="sas-action-btn sas-action-btn--confirm mr-2"
              @mouseenter="startConfirmHover"
              @mouseleave="stopConfirmHover"
              @click="$emit('confirm')"
                  :class="{ 'is-hover': isConfirmHover }"
          >
            <v-icon small color="grey darken-1">mdi-check</v-icon>
          </button>

          <button class="sas-action-btn ml-2" @click="$emit('cancel')">
            <v-icon small color="grey darken-1">mdi-close</v-icon>
          </button>
        </div>

        <div class="d-flex flex-column mt-4">

            <v-chip small class="cursor-pointer d-flex justify-center mb-4" @click="openQuickForm">
              <v-icon small color="grey darken-1" class="mr-1">mdi-plus</v-icon>
              <span class="font-weight-medium grey--text text--darken-2">Création d'agent rapide</span>
            </v-chip>

            <v-chip outlined small class=" cursor-pointer d-flex justify-center mb-2" @click="$emit('select-all')">
              <span class="font-weight-medium grey--text text--darken-2">Tout sélectionner</span>
            </v-chip>

            <v-chip outlined small class="cursor-pointer d-flex justify-center" @click="$emit('unselect-all')">
              <span class="font-weight-medium grey--text text--darken-2">Tout déselectionner</span>
            </v-chip>
        </div>
      </div>
    </v-col>

    <v-col cols="10">
      <!-- Quick Agent Create form -->
      <v-row v-if="showQuickForm" no-gutters class="mb-2">
          <QuickAgentForm v-model="quickForm"
                          @cancel="closeQuickForm"
                          @confirm="handleQuickAgentCreation"
                          into-agency
          />
      </v-row>

      <!-- QuickAgents -->
      <v-row v-if="quickAgents.length > 0" no-gutters>
        <v-col
            v-for="(agent, index) in quickAgents"
            :key="`quick-${index}`"
            cols="3"
            class="quick-agent-wrapper"
        >
          <WorkshopContactCard :infos="getAgentInfos(agent)"
                               :destructible="editable"
                               selected
                               cursor-pointer
                               @delete="removeQuickAgent(index)"
                               @click="onQuickAgentClick()"
          />

        </v-col>
        <v-col cols="3" v-if="!showQuickForm && editable" >
          <div class="pt-2">
            <v-chip small class="cursor-pointer" @click="openQuickForm">
              <v-icon small color="grey darken-1" class="mr-1">mdi-plus</v-icon>
              <span class="font-weight-medium grey--text text--darken-2">Création rapide</span>
            </v-chip>
          </div>
        </v-col>
      </v-row>

      <!-- Agents -->
      <v-row>
        <v-col
            v-for="(column, colIndex) in agentColumns"
            :key="colIndex"
            cols="3"
        >
          <WorkshopContactCard v-for="agent in column" :key="agent.id" :infos="getAgentInfos(agent)"
                               :selected="isSelected(agent.id)"
                               :editable="editable"
                               :active="agent.active"
                               cursor-pointer
                               @click="toggle(agent.id)"
          />
        </v-col>
      </v-row>
    </v-col>

  </v-row>
</template>


<script>

import QuickAgentForm from "@/components/commons/QuickAgentForm.vue";
import WorkshopContactCard from "@/components/commons/WorkshopContactCard.vue";

export default {
  name: "SelectAgentWidget",
  mixins: [],
  data() {
    return {
      isConfirmHover: false,
      showQuickForm: false,
      quickForm: {
        firstname: "",
        lastname: "",
        email: "",
      }
    }
  },
  components: {WorkshopContactCard, QuickAgentForm},
  props: {
    agency: { type: Object, required: true },
    value: { type: Array, default: () => [] },
    editable: { type: Boolean, default: false },
    quickAgents: { type: Array, default: () => [] },
  },
  emits: ["input", "edit", "confirm", "cancel", "update:quickAgents", "select-all", "unselect-all"],
  watch: {},
  mounted() {
    if (!this.editable) { return }
    window.addEventListener("keydown", this.handleKeydown)
  },
  beforeDestroy() {
    if (!this.editable) { return }
    window.removeEventListener("keydown", this.handleKeydown)
  },
  computed: {
    displayedAgents() {
      const agents = this.agency?.agents || []

      if (this.editable) {
        return agents.filter(a => a.firstname || a.lastname)
      }

      return agents.filter(a => this.value.includes(a.id))
    },
    agentColumns() {
      const size = 4
      const agents = this.displayedAgents
      const columns = []

      for (let i = 0; i < agents.length; i += size) {
        columns.push(agents.slice(i, i + size))
      }

      return columns
    },
    quickFormIsValid() {
      return !(
          this.quickForm.firstname.trim().length === 0
          && this.quickForm.lastname.trim().length === 0
          && this.quickForm.email.trim().length === 0
      )
    }
  },
  methods: {
    handleKeydown(e) {
      if (!this.editable) return

      const key = e.key
      const tag = e.target?.tagName
      const isTypingField =
          tag === "INPUT" ||
          tag === "TEXTAREA" ||
          e.target?.isContentEditable

      if (e.key === "Escape") {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (this.showQuickForm === true) {
          this.closeQuickForm()
        } else {
          this.$emit('cancel')
        }
      }
      if (e.key === "Enter") {
        e.preventDefault()
        e.stopImmediatePropagation()
        if (this.showQuickForm === true) {
          this.handleQuickAgentCreation()
          this.closeQuickForm()
        } else {
          this.$emit('confirm')
        }
      }

      if (isTypingField) return

      if (key === "n" || key === "+" || e.code === "NumpadAdd") {
        e.preventDefault()
        e.stopImmediatePropagation()
        this.openQuickForm()
      }
    },
    getAgentInfos(agent) {
      return {
        firstname: agent.firstname || "",
        lastname: agent.lastname || "",
        email: agent.email || "",
      }
    },
    onQuickAgentClick() {
      if (!this.editable) {
        this.$emit('edit')
      }
    },
    toggle(id) {
      if (!this.editable) {
        this.$emit('edit')
        return
      }

      let next = [...this.value]

      if (next.includes(id)) {
        next = next.filter(a => a !== id)
      } else {
        next.push(id)
      }

      this.$emit("input", next)
    },
    isSelected(id) {
      return this.value.includes(id)
    },
    openQuickForm() {
      this.showQuickForm = true
    },
    closeQuickForm() {
      this.showQuickForm = false
      this.resetQuickForm()
    },
    handleQuickAgentCreation() {
      const next = [
        ...this.quickAgents,
        {
          firstname: this.quickForm.firstname.trim(),
          lastname: this.quickForm.lastname.trim(),
          email: this.quickForm.email.trim(),
        }
      ]

      if (this.quickFormIsValid) {
        this.$emit("update:quickAgents", next)
      }
      this.resetQuickForm()
    },
    resetQuickForm() {
      this.showQuickForm = false
      this.quickForm = {
        firstname: "",
        lastname: "",
        email: "",
      }
    },
    removeQuickAgent(index) {
      const next = [...this.quickAgents]
      next.splice(index, 1)
      this.$emit("update:quickAgents", next)
    },
    startConfirmHover() {
      this.isConfirmHover = true
    },
    stopConfirmHover() {
      this.isConfirmHover = false
    },
  }
}
</script>


<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.quick-agent-wrapper {
  padding-right: 18px !important;
  padding-left: 2px !important;
  margin-bottom: 8px;
}

/* Agency card */
.agency-card {
  border-radius: 14px;
  border: 1px solid #b2ebf2 !important;
  transition: border-color 0.2s ease;
}
.agency-card:hover,
.agency-card--linked-hover {
  border-color: #00acc1 !important;
}
.agency-card--inactive {
  opacity: 0.55;
  border-style: dashed !important;
}

.agency-card__icon {
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 999px;
  border: 1px solid #b2ebf2;
  background: #e0f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
}

.agency-card__icon .v-icon {
  color: #00838f;
}

.agency-card__name {
  line-height: 1.1;
}

.agency-card__name, .agency-card__email, .agency-card__line {
  word-break: break-word;
}

.agency-card__email {
  font-size: 10px;
}

.agency-card__email, .agency-card__line {
  font-size: 12px;
  line-height: 1.3;
}

/* Action buttons */
.sas-action-btn {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 999px;
  border: 1px solid #dadce0;
  transition: border-color 0.2s ease;
}

.sas-action-btn:hover {
  background-color: #EEEEEE;
}

.sas-action-btn--confirm:hover, .sas-action-btn--confirm.is-hover {
  border-color: #00acc1 !important;
  background-color: rgba(0, 172, 193, 0.1);
}

.sas-action-btn--confirm:hover .v-icon, .sas-action-btn--confirm.is-hover .v-icon  {
  color: #00838f !important;
}

</style>